/**
 * Base64 decoding utility functions.
 * Some backend endpoints return binary file content (e.g. PDFs) encoded as a
 * base64 text string rather than raw bytes, so it must be decoded client-side
 * before it can be used as a valid Blob.
 */

/**
 * Decodes a base64-encoded string into a Blob with the given MIME type.
 * @param base64 - Base64-encoded file content (no data URI prefix)
 * @param mimeType - MIME type to assign to the resulting Blob (defaults to PDF)
 * @returns A Blob containing the decoded binary data
 */
export function base64ToBlob(base64: string, mimeType = 'application/pdf'): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mimeType });
}

/** Known MIME type -> file extension mapping for download filename correction. */
const MIME_TYPE_EXTENSIONS: Record<string, string> = {
  'application/pdf': 'pdf',
  'application/zip': 'zip',
};

/**
 * Ensures a filename has the extension that matches the given MIME type,
 * replacing any known extension it may already have (e.g. a stale default
 * of ".pdf" when the actual downloaded file is a ".zip").
 * @param filename - Proposed filename (may or may not have an extension)
 * @param mimeType - The actual MIME type of the file content (e.g. from a response header)
 * @returns The filename with an extension matching mimeType, or unchanged if mimeType is not recognized
 */
export function ensureFilenameExtension(filename: string, mimeType: string): string {
  const normalizedMimeType = mimeType.split(';')[0].trim().toLowerCase();
  const extension = MIME_TYPE_EXTENSIONS[normalizedMimeType];
  if (!extension) {
    return filename;
  }
  const knownExtensionPattern = new RegExp(
    `\\.(${Object.values(MIME_TYPE_EXTENSIONS).join('|')})$`,
    'i',
  );
  const baseName = filename.replace(knownExtensionPattern, '');
  return `${baseName}.${extension}`;
}
